import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Search, Plus, Check } from 'lucide-react';

/**
 * Combobox with portal-based dropdown (bypasses overflow-hidden parents)
 * and flexible multi-field search.
 *
 * Props:
 *   items       - array of objects
 *   value       - currently selected valueKey value
 *   onChange    - (id, item) => void
 *   onQuickAdd  - (queryString) => void  — enables "Create X" option when provided
 *   canCreate   - bool
 *   placeholder - string
 *   searchKey   - key to display (default: 'name')
 *   valueKey    - key for the id value (default: 'id')
 *   disabled    - bool
 *   data-testid - string
 */

const DROPDOWN_MAX_H = 300; // approximate max rendered height in px

export function Combobox({
  items = [],
  value,
  onChange,
  onQuickAdd,
  placeholder,
  searchKey = 'name',
  valueKey,
  canCreate = false,
  disabled = false,
  'data-testid': testid,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [pos, setPos] = useState({ top: 0, left: 0, width: 180 });

  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const vKey = valueKey || 'id';

  const selected = items.find(i => i[vKey] === value);
  const displayText = selected ? selected[searchKey] : '';

  // Multi-field search: match query against ANY string field in the item
  const filtered = query
    ? items.filter(i => {
        const q = query.toLowerCase();
        return Object.values(i).some(v => typeof v === 'string' && v.toLowerCase().includes(q));
      })
    : items;

  // Compute fixed position from trigger's bounding rect
  const computePos = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - 8;
    const above = spaceBelow < DROPDOWN_MAX_H && rect.top > DROPDOWN_MAX_H;
    setPos({
      top: above ? rect.top - DROPDOWN_MAX_H - 4 : rect.bottom + 4,
      left: rect.left,
      width: Math.max(rect.width, 180),
    });
  }, []);

  // Close on outside click (checks both trigger and portal dropdown)
  useEffect(() => {
    const handleOutside = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (dropdownRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  // Reposition on scroll/resize while open; focus search input when opened
  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
    setQuery('');
    window.addEventListener('scroll', computePos, true);
    window.addEventListener('resize', computePos);
    return () => {
      window.removeEventListener('scroll', computePos, true);
      window.removeEventListener('resize', computePos);
    };
  }, [open, computePos]);

  const handleToggle = () => {
    if (disabled) return;
    if (!open) computePos(); // compute position before showing
    setOpen(o => !o);
  };

  const handleSelect = (item) => {
    onChange(item[vKey], item);
    setOpen(false);
    setQuery('');
  };

  const handleCreate = () => {
    if (onQuickAdd && query.trim()) {
      onQuickAdd(query.trim());
      setOpen(false);
      setQuery('');
    }
  };

  return (
    <>
      <div ref={triggerRef}>
        <button
          type="button"
          disabled={disabled}
          onClick={handleToggle}
          data-testid={testid}
          className={`w-full flex items-center justify-between border rounded-md px-3 py-2 text-sm bg-white text-left
            ${disabled
              ? 'bg-slate-50 text-slate-400 cursor-not-allowed border-slate-200'
              : 'border-slate-300 hover:border-blue-400 focus:ring-1 focus:ring-blue-500'}
            ${open ? 'border-blue-500 ring-1 ring-blue-500' : ''}`}
        >
          <span className={`flex-1 min-w-0 truncate ${displayText ? 'text-slate-900' : 'text-slate-400'}`}>
            {displayText || placeholder || 'Select...'}
          </span>
          <ChevronDown
            size={14}
            className={`text-slate-400 shrink-0 ml-1 transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
          />
        </button>
      </div>

      {open && createPortal(
        <div
          ref={dropdownRef}
          style={{
            position: 'fixed',
            top: pos.top,
            left: pos.left,
            width: pos.width,
            zIndex: 9999,
          }}
          className="bg-white border border-slate-200 rounded-md shadow-xl overflow-hidden"
        >
          {/* Search input */}
          <div className="p-2 border-b border-slate-100">
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                ref={inputRef}
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Type to search..."
                className="w-full pl-7 pr-3 py-1.5 text-xs border border-slate-200 rounded focus:outline-none focus:border-blue-500"
              />
            </div>
          </div>

          {/* Options list */}
          <ul className="max-h-64 overflow-y-auto">
            {filtered.map(item => (
              <li
                key={item[vKey]}
                onClick={() => handleSelect(item)}
                className={`px-3 py-2 text-sm cursor-pointer hover:bg-blue-50 flex items-center justify-between
                  ${value === item[vKey] ? 'bg-blue-50 text-blue-700 font-medium' : 'text-slate-700'}`}
              >
                <span className="flex-1 min-w-0">
                  <span className="block truncate">{item[searchKey]}</span>
                  {item.gst && (
                    <span className="text-[10px] text-slate-400 font-mono">{item.gst}</span>
                  )}
                </span>
                {value === item[vKey] && (
                  <Check size={13} className="text-blue-600 shrink-0 ml-2" />
                )}
              </li>
            ))}

            {/* Quick-add option */}
            {canCreate && query && !filtered.some(i => i[searchKey]?.toLowerCase() === query.toLowerCase()) && (
              <li
                onClick={handleCreate}
                className="px-3 py-2 text-sm cursor-pointer hover:bg-green-50 text-green-700 font-medium border-t border-slate-100 flex items-center gap-2"
                data-testid="combobox-create-option"
              >
                <Plus size={13} /> Create "{query}"
              </li>
            )}

            {/* Empty state */}
            {filtered.length === 0 && !canCreate && (
              <li className="px-3 py-2 text-sm text-slate-400 text-center">No results</li>
            )}
            {filtered.length === 0 && canCreate && !query && (
              <li className="px-3 py-2 text-sm text-slate-400 text-center">
                Type to search or add new...
              </li>
            )}
          </ul>
        </div>,
        document.body
      )}
    </>
  );
}

export default Combobox;
